import { useEffect, useRef, type ReactElement, type ReactNode } from 'react';
import { NeonCardStandard } from '@ps-refarch-ux/neon';
import { StickyFormFooter } from '@/components/StickyFormFooter/sticky-form-footer';
import type { StickyFormFooterProps } from '@/components/StickyFormFooter/sticky-form-footer';
import { DATA_SLOT } from '@/constants/neon-constants';
import { usePageFooter } from '@/context';
import styles from './multi-section-form-layout.module.scss';

export interface MultiSectionFormLayoutProps {
  children: ReactNode;
  /** Cancel/save (and optional tertiary); rendered in staff footer slot. */
  footer: StickyFormFooterProps;
  /** Root `data-testid`. */
  formTestId?: string;
  /** Additional class applied to the inner content column for page-specific layout overrides. */
  contentClassName?: string;
  /** Additional class applied to the stacked form for page-specific spacing overrides. */
  formClassName?: string;
}

/** Fields in a scrollable card; actions in the staff footer slot (full width). */
export function MultiSectionFormLayout({
  children,
  footer,
  formTestId = 'staff-multi-section-form',
  contentClassName,
  formClassName,
}: MultiSectionFormLayoutProps): ReactElement {
  const { setFooterNode } = usePageFooter();
  const footerRef = useRef(footer);
  footerRef.current = footer;

  useEffect(() => {
    setFooterNode(
      <NeonCardStandard
        className={styles.formActionsCard}
        dataInjectionSpacing="false"
        style={{ borderRadius: 0 }}
      >
        <div data-slot={DATA_SLOT} className={styles.formActionsCardBody}>
          <StickyFormFooter
            cancelLabel={footer.cancelLabel}
            saveLabel={footer.saveLabel}
            onCancel={() => footerRef.current.onCancel()}
            onSave={() => footerRef.current.onSave()}
            saveDisabled={footer.saveDisabled}
            primaryButton={
              footer.primaryButton
                ? {
                    id: footer.primaryButton.id,
                    text: footer.primaryButton.text,
                    disabled: footer.primaryButton.disabled,
                    onClick: () => footerRef.current.primaryButton?.onClick(),
                  }
                : undefined
            }
            tertiaryButton={
              footer.tertiaryButton
                ? {
                    id: footer.tertiaryButton.id,
                    text: footer.tertiaryButton.text,
                    disabled: footer.tertiaryButton.disabled,
                    onClick: () => footerRef.current.tertiaryButton?.onClick(),
                  }
                : undefined
            }
            footerId={footer.footerId}
            cancelButtonId={footer.cancelButtonId}
            saveButtonId={footer.saveButtonId}
          />
        </div>
      </NeonCardStandard>
    );
    return () => setFooterNode(null);
  }, [
    setFooterNode,
    footer.cancelLabel,
    footer.saveLabel,
    footer.saveDisabled,
    footer.footerId,
    footer.cancelButtonId,
    footer.saveButtonId,
    footer.tertiaryButton,
    footer.primaryButton,
  ]);

  return (
    <div data-testid={formTestId} className={styles.formLayoutRoot}>
      <NeonCardStandard
        className={styles.formFieldsCard}
        dataFullHeight="true"
        dataInjectionSpacing="false"
      >
        <div data-slot={DATA_SLOT} className={styles.formFieldsCardBody}>
          <div className={styles.formScrollRegion}>
            <div
              className={`${styles.formContentInner}${contentClassName ? ` ${contentClassName}` : ''}`}
            >
              <div className={`${styles.form}${formClassName ? ` ${formClassName}` : ''}`}>
                {children}
              </div>
            </div>
          </div>
        </div>
      </NeonCardStandard>
    </div>
  );
}
